import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Check, X, Edit, AlertCircle, Clock, CheckCircle, XCircle, FlaskConical, Loader2, Filter, RotateCcw } from "lucide-react";

const ApproveHoursTab = ({ user }) => {
  const [pendingHours, setPendingHours] = useState([]);
  const [approvedHours, setApprovedHours] = useState([]);
  const [rejectedHours, setRejectedHours] = useState([]);
  const [selectedHours, setSelectedHours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingHour, setEditingHour] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [activeSubTab, setActiveSubTab] = useState("pending");
  const [flaggingTest, setFlaggingTest] = useState({});
  const [editFormData, setEditFormData] = useState({
    date: "",
    start_time: "",
    hours: "",
    activity_type: "",
    description: ""
  });

  // Filters
  const [testFilter, setTestFilter] = useState("all"); // "all", "real", "test"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");

  useEffect(() => {
    fetchAllHours();
  }, []);

  const fetchAllHours = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Fetch pending hours
      const pendingRes = await axios.get(`${API}/admin/hours/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingHours(pendingRes.data);
      
      // Fetch approved hours
      const approvedRes = await axios.get(`${API}/admin/hours/approved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovedHours(approvedRes.data);
      
      // Fetch rejected hours
      const rejectedRes = await axios.get(`${API}/admin/hours/rejected`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRejectedHours(rejectedRes.data);
      
    } catch (error) {
      console.error("Failed to fetch hours:", error);
    }
  };

  const toggleSelectHour = (hourId) => {
    setSelectedHours(prev => 
      prev.includes(hourId) 
        ? prev.filter(id => id !== hourId)
        : [...prev, hourId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedHours.length === filteredPending.length) {
      setSelectedHours([]);
    } else {
      setSelectedHours(filteredPending.map(h => h.id));
    }
  };

  const handleEditClick = (hour) => {
    setEditingHour(hour);
    setEditFormData({
      date: hour.date,
      start_time: hour.start_time,
      hours: hour.hours,
      activity_type: hour.activity_type,
      description: hour.description || ""
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/hours/${editingHour.id}/edit`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Hour details updated");
      setEditDialogOpen(false);
      fetchAllHours();
    } catch (error) {
      toast.error("Failed to update hour");
    }
  };

  const handleApprove = async (hourId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/hours/${hourId}/approve`, 
        { status: "approved" },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Hour approved!");
      fetchAllHours();
      setSelectedHours(prev => prev.filter(id => id !== hourId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedHours.length === 0) {
      toast.error("No hours selected");
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/hours/batch-approve`, 
        { hour_ids: selectedHours },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`${selectedHours.length} hours approved!`);
      setSelectedHours([]);
      fetchAllHours();
    } catch (error) {
      toast.error("Failed to bulk approve");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = (hour) => {
    setEditingHour(hour);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/hours/${editingHour.id}/approve`, 
        { status: "rejected", rejection_reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Hour rejected");
      setRejectDialogOpen(false);
      fetchAllHours();
    } catch (error) {
      toast.error("Failed to reject hour");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTestFlag = async (hourId, currentIsTest) => {
    const newIsTest = !currentIsTest;
    
    // Optimistic update — immediately flip the flag in local state
    const updateList = (list) => list.map(h => h.id === hourId ? { ...h, is_test: newIsTest } : h);
    setPendingHours(prev => updateList(prev));
    setApprovedHours(prev => updateList(prev));
    setRejectedHours(prev => updateList(prev));

    setFlaggingTest(prev => ({ ...prev, [hourId]: true }));
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/hours/${hourId}/toggle-test`, 
        { is_test: newIsTest },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(newIsTest ? "Marked as test data" : "Unmarked as test data");
      await fetchAllHours();
    } catch (error) {
      toast.error("Failed to update test flag");
      // Revert optimistic update on failure
      const revertList = (list) => list.map(h => h.id === hourId ? { ...h, is_test: currentIsTest } : h);
      setPendingHours(prev => revertList(prev));
      setApprovedHours(prev => revertList(prev));
      setRejectedHours(prev => revertList(prev));
    } finally {
      setFlaggingTest(prev => ({ ...prev, [hourId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Extract unique regions from all hours for the filter dropdown
  const allRegions = useMemo(() => {
    const regions = new Set();
    [...pendingHours, ...approvedHours, ...rejectedHours].forEach(h => {
      if (h.club_county) regions.add(h.club_county);
    });
    return Array.from(regions).sort();
  }, [pendingHours, approvedHours, rejectedHours]);

  // Filter function shared across all tabs
  const applyFilters = (hours) => {
    return hours.filter(h => {
      // Test data filter
      if (testFilter === "real" && h.is_test) return false;
      if (testFilter === "test" && !h.is_test) return false;
      // Date range filter
      if (dateFrom && h.date < dateFrom) return false;
      if (dateTo && h.date > dateTo) return false;
      // Region filter
      if (regionFilter !== "all" && h.club_county !== regionFilter) return false;
      return true;
    });
  };

  const filteredPending = useMemo(() => applyFilters(pendingHours), [pendingHours, testFilter, dateFrom, dateTo, regionFilter]);
  const filteredApproved = useMemo(() => applyFilters(approvedHours), [approvedHours, testFilter, dateFrom, dateTo, regionFilter]);
  const filteredRejected = useMemo(() => applyFilters(rejectedHours), [rejectedHours, testFilter, dateFrom, dateTo, regionFilter]);

  const hasActiveFilters = testFilter !== "all" || dateFrom || dateTo || regionFilter !== "all";

  const clearFilters = () => {
    setTestFilter("all");
    setDateFrom("");
    setDateTo("");
    setRegionFilter("all");
  };

  const HourCard = ({ hour, showActions = true, showApprovalInfo = false }) => (
    <div 
      data-testid={`hour-card-${hour.id}`}
      className={`border rounded-lg p-4 bg-white hover:shadow-md transition-shadow ${
        hour.is_test ? 'border-orange-300 bg-orange-50' : ''
      }`}
    >
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="font-semibold text-gray-800">{hour.volunteer_name}</span>
            <Badge variant="outline" className="text-xs">
              {hour.club_name}
            </Badge>
            {hour.self_logged && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">
                Self-logged
              </Badge>
            )}
            {hour.is_test && (
              <Badge className="bg-orange-100 text-orange-700 text-xs">
                <FlaskConical className="w-3 h-3 mr-1" />
                Test Data
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600 mb-2">
            <div>
              <span className="text-gray-400">Date:</span> {formatDate(hour.date)}
            </div>
            <div>
              <span className="text-gray-400">Time:</span> {hour.start_time}
            </div>
            <div>
              <span className="text-gray-400">Activity:</span> <span className="capitalize">{hour.activity_type}</span>
            </div>
            <div>
              <span className="text-gray-400">Hours:</span> <span className="font-bold text-green-600">{hour.hours}h</span>
            </div>
          </div>
          
          {hour.description && (
            <p className="text-sm text-gray-500 mb-2 line-clamp-2">{hour.description}</p>
          )}
          
          {showApprovalInfo && (
            <div className="text-xs text-gray-400 mt-2 space-y-1">
              {hour.supervisor_name && (
                <p>Club approved by: {hour.supervisor_name} {hour.supervisor_reviewed_at && `on ${formatDateTime(hour.supervisor_reviewed_at)}`}</p>
              )}
              {hour.admin_reviewed_at && (
                <p>Platform reviewed: {formatDateTime(hour.admin_reviewed_at)}</p>
              )}
              {hour.rejection_reason && (
                <p className="text-red-500">Reason: {hour.rejection_reason}</p>
              )}
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-2 flex-shrink-0">
          {showActions && (
            <>
              <Button
                size="sm"
                onClick={() => handleApprove(hour.id)}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid={`approve-btn-${hour.id}`}
              >
                <Check className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRejectClick(hour)}
                disabled={loading}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid={`reject-btn-${hour.id}`}
              >
                <X className="w-4 h-4 mr-1" />
                Reject
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEditClick(hour)}
                className="border-gray-300"
                data-testid={`edit-btn-${hour.id}`}
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
            </>
          )}
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleToggleTestFlag(hour.id, hour.is_test)}
            disabled={flaggingTest[hour.id]}
            className={hour.is_test 
              ? "border-green-300 text-green-600 hover:bg-green-50" 
              : "border-orange-300 text-orange-600 hover:bg-orange-50"
            }
            data-testid={`toggle-test-btn-${hour.id}`}
          >
            {flaggingTest[hour.id] ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <FlaskConical className="w-4 h-4 mr-1" />
                {hour.is_test ? "Unmark Test" : "Mark Test"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="approve-hours-tab">
      {/* Filter Bar */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-xs h-7 text-gray-500" data-testid="clear-filters-btn">
                <RotateCcw className="w-3 h-3 mr-1" />
                Clear filters
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Data Type</Label>
              <Select value={testFilter} onValueChange={setTestFilter}>
                <SelectTrigger className="h-9" data-testid="test-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hours</SelectItem>
                  <SelectItem value="real">Real Hours Only</SelectItem>
                  <SelectItem value="test">Test Hours Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">From Date</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="date-from-filter" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">To Date</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="date-to-filter" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Region</Label>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="h-9" data-testid="region-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {allRegions.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid grid-cols-3 mb-4">
          <TabsTrigger value="pending" className="relative" data-testid="pending-tab-trigger">
            <Clock className="w-4 h-4 mr-2" />
            Pending
            {filteredPending.length > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {filteredPending.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="approved-tab-trigger">
            <CheckCircle className="w-4 h-4 mr-2" />
            Approved
            <span className="ml-2 text-xs text-gray-500">({filteredApproved.length})</span>
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="rejected-tab-trigger">
            <XCircle className="w-4 h-4 mr-2" />
            Rejected
            <span className="ml-2 text-xs text-gray-500">({filteredRejected.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* Pending Approval Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Pending Platform Approval</CardTitle>
              <CardDescription>
                Hours approved by club supervisors, awaiting your approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredPending.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{hasActiveFilters ? "No hours match current filters" : "No hours pending approval"}</p>
                </div>
              ) : (
                <>
                  {/* Bulk Actions */}
                  <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedHours.length === filteredPending.length && filteredPending.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm text-gray-600">
                        {selectedHours.length} of {filteredPending.length} selected
                      </span>
                    </div>
                    {selectedHours.length > 0 && (
                      <Button
                        onClick={handleBulkApprove}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approve Selected ({selectedHours.length})
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {filteredPending.map((hour) => (
                      <div key={hour.id} className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedHours.includes(hour.id)}
                          onChange={() => toggleSelectHour(hour.id)}
                          className="w-4 h-4 rounded mt-5"
                        />
                        <div className="flex-1">
                          <HourCard hour={hour} showActions={true} />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-green-700">Platform Approved Hours</CardTitle>
              <CardDescription>
                Hours you have approved - these can be purchased by customers
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredApproved.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{hasActiveFilters ? "No hours match current filters" : "No approved hours yet"}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredApproved.map((hour) => (
                    <HourCard 
                      key={hour.id} 
                      hour={hour} 
                      showActions={false} 
                      showApprovalInfo={true}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-red-700">Rejected Hours</CardTitle>
              <CardDescription>
                Hours you have rejected at platform level
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredRejected.length === 0 ? (
                <div className="text-center py-12">
                  <XCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{hasActiveFilters ? "No hours match current filters" : "No rejected hours"}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredRejected.map((hour) => (
                    <HourCard 
                      key={hour.id} 
                      hour={hour} 
                      showActions={false} 
                      showApprovalInfo={true}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Hour Details</DialogTitle>
            <DialogDescription>
              Modify the volunteer hour entry before approval
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                />
              </div>
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={editFormData.start_time}
                  onChange={(e) => setEditFormData({...editFormData, start_time: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hours</Label>
                <Input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={editFormData.hours}
                  onChange={(e) => setEditFormData({...editFormData, hours: e.target.value})}
                />
              </div>
              <div>
                <Label>Activity Type</Label>
                <Select 
                  value={editFormData.activity_type} 
                  onValueChange={(value) => setEditFormData({...editFormData, activity_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coaching">Coaching</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="community work">Community Work</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              <AlertCircle className="w-5 h-5 inline mr-2" />
              Reject Hour Entry
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this hour entry
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Rejection Reason</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this entry is being rejected..."
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReject}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Reject Hour
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApproveHoursTab;
